import React, { useState, useEffect } from "react";
import { Box, useTheme, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import Header from "../../components/Header";
import {
  getAuth
} from "firebase/auth";
import {
  Timestamp,
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  onSnapshot
} from "firebase/firestore";
import { TextField, Button, Card, CardContent, Typography, MenuItem, Select, InputLabel, FormControl, CircularProgress, Container } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { tokens } from "../../theme";

import { useUser } from "../../common/UserContext";

import { useAccount } from "../../common/AccountContext";

import { createUser, sendPasswordResetLink } from "../../common/FunctionCalls";

import { useNavigate } from 'react-router-dom';

import lockImage from "../../images/website-images/lock.png"

import { PLANS } from "../../planConfig";

import { resetPassword } from "../../common/Authenticator";

const UserAccounts = () => {
  const auth = getAuth();
  const db = getFirestore();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { appUserAccountNumber, appUserEmail } = useUser();

  const { accountSubscriptionType, accountSubscriptionPlan } = useAccount();

  const [resetEmailSentDialog, setResetEmailSentDialog] = useState(false);

  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    role: "member",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [users, setUsers] = useState([]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteEmail, setDeleteEmail] = useState(null);

  const navigate = useNavigate();
  
  const [maxPlanUserAccounts,setMaxPlanUserAccounts] = useState(0);

  const [maxUsersDialogOpen, setMaxUsersDialogOpen] = useState(false);

  useEffect(() => {
    if (!accountSubscriptionPlan) return;
  
    const maxPlanUserAccountsAllowed = PLANS.planAdditionaInfo[accountSubscriptionPlan].maxUserAccounts;
  
    setMaxPlanUserAccounts(maxPlanUserAccountsAllowed);

}, [accountSubscriptionPlan]);


  useEffect(() => {
    if (!appUserAccountNumber) return;
  
    const q = query(
      collection(db, "appUsers"),
      where("accountNumber", "==", appUserAccountNumber),
      where("status", "!=", "deleted")
    );
  
    // Set up real-time listener
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const userList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(userList);
    });
  
    // Cleanup listener on unmount or when accountNumber changes
    return () => unsubscribe();
  }, [appUserAccountNumber]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
  
    try {
      // Check current number of users under the account
      const userCountQuery = await getDocs(
        query(collection(db, "appUsers"), where("accountNumber", "==", appUserAccountNumber), where("status", "!=", "deleted"))
      );
  
      if (userCountQuery.size >= maxPlanUserAccounts) {
        setMaxUsersDialogOpen(true);
        setLoading(false);
        return;
      }
  
      const existingUserQuery = await getDocs(
        query(
          collection(db, "appUsers"),
          where("email", "==", form.email),
          where("accountNumber", "==", appUserAccountNumber)
        )
      );
  
      if (!existingUserQuery.empty) {
        const userDoc = existingUserQuery.docs[0];
        const userData = userDoc.data();
  
        if (userData.status === "deleted") {
          const ref = doc(db, "appUsers", userDoc.id);
          await updateDoc(ref, {
            status: "active",
            firstName: form.firstName,
            lastName: form.lastName,
            fullName: form.firstName + " " + form.lastName,
            role: form.role,
            accountNumber: appUserAccountNumber,
            createdBy: appUserEmail,
            createdDate: Timestamp.now()
          });
  
          try {
            await sendPasswordResetLink(form.email, form.firstName, auth);
            setMessage("User reactivated and password reset email sent.");
            setForm({ email: "", firstName: "", lastName: "", password: "", role: "member" });
          } catch (error) {
            console.error("Error sending password reset link:", error);
          }
  
          setLoading(false);
          return;
        } else {
          setMessage("A user with this email already exists.");
          setLoading(false);
          return;
        }
      }
  
      try {
        await createUser(
          form.email,
          form.password,
          form.firstName,
          form.lastName,
          form.role,
          appUserAccountNumber,
          appUserEmail,
          auth
        );
  
        setMessage("User has been created and an email sent to the user to reset their password.");
        setForm({ email: "", firstName: "", lastName: "", password: "", role: "member" });
      } catch (error) {
        console.error("Error calling function:", error);
      }
    } catch (error) {
      console.error("Error creating user:", error);
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };
  

  const handleResetPassword = async (e,email) => {

    e.preventDefault();

    try {

     await resetPassword(email);

     setResetEmailSentDialog(true);
    
    } catch (error) {
      console.error("Reset Password Error:", error);
    }
  };

  const handleConfirmClose = async () => {
    
    try {
 
        try {
          if (deleteId)
          {
            const userQuery = await getDocs(
              query(
                collection(db, "appUsers"),
                where("email", "==", deleteEmail),
                where("accountNumber", "==", appUserAccountNumber)
              )
            );
            

              userQuery.forEach(async (userDoc) => {
                const ref = doc(db, "appUsers", userDoc.id);
                await updateDoc(ref, { status: "deleted" });
              });

              setMessage("User marked as deleted.");
        
          }

        } catch (err) {
          console.error("Delete error:", err);
        }

      setConfirmOpen(false);
      setDeleteId("");
      setDeleteEmail("");
    } catch (error) {
      console.error("Error deleting document: ", error);
    }
  };

  const handleCancelClose = () => {
    setConfirmOpen(false);
    setDeleteId("");
    setDeleteEmail("");
  };

  const handleDelete = async (id, email) => {

    setDeleteId(id);
    setDeleteEmail(email);
    setConfirmOpen(true);

  };

  const handleEdit = async (params) => {
    if (!["admin", "member"].includes(params.role)) {
      setMessage("Role must be either 'admin' or 'member'.");
      return;
    }

    try {
      const ref = doc(db, "appUsers", params.id);
      await updateDoc(ref, {
        firstName: params.firstName,
        lastName: params.lastName,
        role: params.role,
      });
     
    } catch (err) {
      console.error("Edit error:", err);
    }
  };

  const columns = [
    { field: "email", headerName: "Email", flex: 1 },
    {
      field: "firstName",
      headerName: "First Name",
      flex: 1,
      editable: true,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{params.value}</span>
          <span role="img" aria-label="edit">✏️</span>
        </div>
      ),
    },
    {
      field: "lastName",
      headerName: "Last Name",
      flex: 1,
      editable: true,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{params.value}</span>
          <span role="img" aria-label="edit">✏️</span>
        </div>
      ),
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      editable: true,
      type: "singleSelect",
      valueOptions: ["admin", "member"],
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{params.value}</span>
          <span role="img" aria-label="edit">✏️</span>
        </div>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
        
          {params.row.email !== appUserEmail && (
            <>

            <Button
              size="small"
              variant="contained"
              color="secondary"
              sx={{margin:0, paddingLeft:7, paddingRight:7, fontWeight:'bold'}} 
              fullWidth
              onClick={(e) => handleResetPassword(e, params.row.email)}
            >
              Reset Password
            </Button>

            <Button size="small" 
            fullWidth 
            variant="contained" color="error" onClick={() => handleDelete(params.id, params.row.email)}>
              Delete
            </Button>
            
            </>
          )}

          {params.row.email === appUserEmail && (
              <Typography variant="h6" sx={{textAlign:'center'}}>
              (You)
            </Typography>
      
          )}

        </div>
      ),
    },
  ];

  return (
    <>
    {(!accountSubscriptionType || accountSubscriptionType.length ==0)  ? (
      <Container maxWidth="sm" sx={{ mt: 10, textAlign: 'center' }}>
          

        <CircularProgress size={180}/>
      </Container>
      ) : (
      <>
      <Box m="20px">
        <Header title="User Accounts" subtitle="Manage user accounts" />
        <Box height="75vh">
          <div style={{ maxWidth: "900px", margin: "2rem auto" }}>
          {accountSubscriptionType === 'trial' ? (
              <>
              <Card>
                <CardContent style={{
                  backgroundColor: colors.grey[700], // solid background color
                  color: colors.primary[100],
                  padding: '2rem',
                  textAlign: 'center',
                  borderRadius: '12px', 

                  backgroundImage: `url(${lockImage})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: '100px'
                }}>
                  <Typography variant="h2" gutterBottom>
                    <b>Locked</b>
                  </Typography>
                  <Typography variant="body1" style={{ fontSize: '18px', marginTop: '1rem' }}>
                    This section allows you to create additional application logins for your team based on your subscription plan. 
                    As you're currently on a Trial plan, this functionality is locked. 
                    To unlock it, please upgrade to a full subscription.
                    <br></br><br></br>

                        <Button
                          variant="contained"
                          onClick={() => navigate('/pricing')}
                          sx={{
                            backgroundColor: "#db5500",
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: "20px",
                            textTransform: "none",
                            '&:hover': {
                              backgroundColor: "#e66a00",
                            },
                          }}
                        >
                          Ungrade Your Plan
                        </Button>
                  </Typography>
                </CardContent>
              </Card>
              </>
            ) : (
              <>
              <Card>
                <CardContent style={{backgroundColor:colors.inputModal.inputBackgroundColor}}>
                  <Typography variant="h5" gutterBottom>
                    Create User Account
                  </Typography>
                  <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <TextField
                      label="Email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required 
                      inputProps={{ style: { fontSize:'20px' }}} sx={{'& .MuiOutlinedInput-root': {backgroundColor: colors.inputModal.inputBackgroundColor,'& fieldset': {borderColor: colors.inputModal.inputDefaultBorderColor}, '&:hover fieldset': {borderColor: colors.inputModal.inputHoverBorderColor,},'&.Mui-focused fieldset': {borderColor: colors.inputModal.inputFocusedBorderColor,},},'& .MuiInputLabel-root': {color:colors.inputModal.inputDefaultBorderColor, '&:hover': {color: colors.inputModal.inputHoverBorderColor,},'&.Mui-focused': {color: 'currentColor',},},}}  
                    />
                    <TextField
                      label="First Name"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      required 
                      inputProps={{ style: { fontSize:'20px' }}} sx={{'& .MuiOutlinedInput-root': {backgroundColor: colors.inputModal.inputBackgroundColor,'& fieldset': {borderColor: colors.inputModal.inputDefaultBorderColor}, '&:hover fieldset': {borderColor: colors.inputModal.inputHoverBorderColor,},'&.Mui-focused fieldset': {borderColor: colors.inputModal.inputFocusedBorderColor,},},'& .MuiInputLabel-root': {color:colors.inputModal.inputDefaultBorderColor, '&:hover': {color: colors.inputModal.inputHoverBorderColor,},'&.Mui-focused': {color: 'currentColor',},},}}  
                    />
                    <TextField
                      label="Last Name"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      required 
                      inputProps={{ style: { fontSize:'20px' }}} sx={{'& .MuiOutlinedInput-root': {backgroundColor: colors.inputModal.inputBackgroundColor,'& fieldset': {borderColor: colors.inputModal.inputDefaultBorderColor}, '&:hover fieldset': {borderColor: colors.inputModal.inputHoverBorderColor,},'&.Mui-focused fieldset': {borderColor: colors.inputModal.inputFocusedBorderColor,},},'& .MuiInputLabel-root': {color:colors.inputModal.inputDefaultBorderColor, '&:hover': {color: colors.inputModal.inputHoverBorderColor,},'&.Mui-focused': {color: 'currentColor',},},}}  
                    />
                    <TextField
                      label="Temporary Password"
                      type="password"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      required 
                      inputProps={{ style: { fontSize:'20px' }}} sx={{'& .MuiOutlinedInput-root': {backgroundColor: colors.inputModal.inputBackgroundColor,'& fieldset': {borderColor: colors.inputModal.inputDefaultBorderColor}, '&:hover fieldset': {borderColor: colors.inputModal.inputHoverBorderColor,},'&.Mui-focused fieldset': {borderColor: colors.inputModal.inputFocusedBorderColor,},},'& .MuiInputLabel-root': {color:colors.inputModal.inputDefaultBorderColor, '&:hover': {color: colors.inputModal.inputHoverBorderColor,},'&.Mui-focused': {color: 'currentColor',},},}}  
                    />
                    <FormControl fullWidth>
                      <InputLabel id="role-label">Role</InputLabel>
                      <Select
                        labelId="role-label"
                        name="role"
                        value={form.role}
                        onChange={handleChange}
                        label="Role" 
                        style = {{fontSize:'20px' }}
                      >
                        <MenuItem value="admin">admin</MenuItem>
                        <MenuItem value="member">member</MenuItem>
                      </Select>
                    </FormControl>
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Button type="submit" variant="contained" disabled={loading} sx={{ maxWidth:'30%' , backgroundColor: colors.greenAccent[600], color: colors.primary[100],'&:hover': { backgroundColor: colors.greenAccent[800] }, height: '98%',fontSize:'20px',margin:'0px',padding:'0px' }}>
                      &nbsp; &nbsp; {loading ? "Creating..." : "Create User"} &nbsp; &nbsp;
                      </Button>
                    </Box>
                  </form>
                  {message && <Typography variant="body2" style={{ marginTop: "1rem" }}>{message}</Typography>}
                </CardContent>
              </Card>

              <div style={{ marginTop: "3rem", height: 500 }}>
                <Typography variant="h6" gutterBottom>
                  Existing Users
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                💡 Tip: Click on the First Name, Last Name, or Role cells to edit them directly.
              </Typography>
                <DataGrid
                  rows={users}
                  columns={columns}
                  pageSize={5}  
                  rowHeight={100}  
                  sx={{
                    "& .MuiDataGrid-root": { border: "none" },
                    "& .MuiDataGrid-cell": { borderBottom: "none" },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: theme.palette.mode === "dark" ? colors.blueAccent[700] : "#d0d0dd",
                      borderBottom: "none",
                      color: theme.palette.mode === "dark" ? colors.grey[100] : "#333",
                      fontWeight: "bold",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                      backgroundColor: theme.palette.mode === "dark" ? colors.blueAccent[700] : "#d0d0dd",
                      color: theme.palette.mode === "dark" ? colors.grey[100] : "#333",
                    },
                    "& .MuiCheckbox-root": {
                      color: `${theme.palette.mode === "dark" ? colors.greenAccent[200] : "#555"} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                      color: `${theme.palette.mode === "dark" ? colors.grey[100] : "#333"} !important`,
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: theme.palette.mode === "dark" ? colors.primary[500] : "#f5f5f5",
                    },
                    "& .MuiDataGrid-cell:hover": {
                      backgroundColor: theme.palette.mode === "dark" ? colors.primary[300] : "#e0e0ff",
                      cursor: "pointer",
                    },
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                  rowsPerPageOptions={[5]}
                  processRowUpdate={(newRow, oldRow) => {
                    const updatedRow = { ...oldRow, ...newRow, email: oldRow.email }; // This prevents the email from being edited
                    handleEdit(updatedRow);
                    return updatedRow;
                  }}
                  experimentalFeatures={{ newEditingApi: true }}
                />
              </div>
              </>
      )}
          </div>
        </Box>

        <Dialog open={confirmOpen} onClose={handleCancelClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title" sx={{ backgroundColor: colors.greenAccent[700] }}>{"Confirm"}</DialogTitle>
          <DialogContent sx={{ backgroundColor: colors.greenAccent[700] }}>
            <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the user with email: {deleteEmail}?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: colors.greenAccent[700] }}>
          <Button onClick={handleCancelClose} style={{color:'#FFFFFF',backgroundColor:"#cc3030"}}>
              No
            </Button>
            <Button onClick={handleConfirmClose} style={{color:'#FFFFFF',backgroundColor:"#cc3030"}} autoFocus>
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
            open={maxUsersDialogOpen}
            onClose={() => setMaxUsersDialogOpen(false)}
            aria-labelledby="max-users-dialog-title"
            aria-describedby="max-users-dialog-description"
          >
            <DialogTitle id="max-users-dialog-title" sx={{ backgroundColor: colors.greenAccent[700] }}>
              {"Maximum Users Reached"}
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: colors.greenAccent[700] }}>
              <DialogContentText id="max-users-dialog-description">
                You’ve reached the maximum number of users allowed by your current plan ({maxPlanUserAccounts} users).
                You can delete existing users before adding new ones or upgrade your plan for more capacity.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: colors.greenAccent[700] }}>
              <Button onClick={() => setMaxUsersDialogOpen(false)} style={{ color: '#FFFFFF', backgroundColor: "#cc3030" }}>
                OK
              </Button>
              <Button onClick={() => { setMaxUsersDialogOpen(false); navigate('/pricing'); }} style={{ color: '#FFFFFF', backgroundColor: "#db5500" }}>
                Upgrade Plan
              </Button>
            </DialogActions>
          </Dialog>


          <Dialog
            open={resetEmailSentDialog}
            onClose={() => setResetEmailSentDialog(false)}
            aria-labelledby="reset-password-dialog-title"
            aria-describedby="reset-password-dialog-description"
          >
            <DialogTitle id="reset-password-dialog-title" sx={{ backgroundColor: colors.greenAccent[700] }}>
              {"Password Reset Email Sent"}
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: colors.greenAccent[700] }}>
              <DialogContentText id="reset-password-dialog-description">
                A reset password link has been sent to the user’s email.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: colors.greenAccent[700] }}>
              <Button onClick={() => setResetEmailSentDialog(false)} style={{ color: '#FFFFFF', backgroundColor: "#cc3030" }}>
                OK
              </Button>
            </DialogActions>
          </Dialog>


      </Box>
      </>
    )}
    </>
  );
};

export default UserAccounts;
